import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { useTranslation } from "react-i18next";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import topicsIndex from "./data/index.json";
import {
  normalizeTopics,
  dedupeTopicsByUrlKeyPreferCustomer,
} from "./data/topicUtils";

const rawTopics = Array.isArray(topicsIndex) ? topicsIndex : [];
const normalizedTopics = normalizeTopics(rawTopics);
const PUBLIC_URL = process.env.PUBLIC_URL || "";

function triggerPrintWithContent(htmlContent) {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("style", "position:absolute;width:0;height:0;border:0;");
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!doc) {
    document.body.removeChild(iframe);
    return;
  }
  const printDoc = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: system-ui, sans-serif; line-height: 1.5; max-width: 100%; padding: 1rem; color: #222; }
    img { max-width: 100%; height: auto; }
    table { max-width: 100%; }
    pre { white-space: pre-wrap; word-wrap: break-word; }
  </style>
</head>
<body>${htmlContent || ""}</body>
</html>`;
  doc.open();
  doc.write(printDoc);
  doc.close();

  const remove = () => {
    try {
      if (document.body.contains(iframe)) document.body.removeChild(iframe);
    } catch (_) {}
  };

  const doPrint = () => {
    iframe.contentWindow?.focus?.();
    iframe.contentWindow?.print?.();
    setTimeout(remove, 2000);
  };

  // Wait for all images to finish loading before printing
  iframe.addEventListener("load", () => {
    const images = Array.from(doc.images || []);
    const pending = images.filter((img) => !img.complete);
    if (pending.length === 0) {
      doPrint();
      return;
    }
    let resolved = 0;
    const onSettled = () => {
      resolved += 1;
      if (resolved >= pending.length) doPrint();
    };
    pending.forEach((img) => {
      img.addEventListener("load", onSettled, { once: true });
      img.addEventListener("error", onSettled, { once: true });
    });
    // Safety timeout – print even if some images fail to load
    setTimeout(doPrint, 8000);
  });
}

const FAQDetail = () => {
  const { urlKey } = useParams();
  const { t } = useTranslation();
  const auth = useJumboAuth();
  const authUser = auth?.getAuthUser?.() || {};
  const customerType =
    authUser?.customer?.customerType ?? authUser?.customer?.customer_type;
  const isCustomerType2Or3 =
    Number(customerType) === 2 || Number(customerType) === 3;
  const userTyp = authUser?.type ?? authUser?.typ;
  const customerId =
    authUser?.customerId ??
    authUser?.customer?.customerId ??
    authUser?.customer?.id;
  // Internal topics are readable only by the main administrator (customerId 1 + type 1).
  const isMainAdmin = Number(userTyp) === 1 && Number(customerId) === 1;

  const [content, setContent] = useState("");
  const [loadError, setLoadError] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const printTriggeredRef = useRef(false);
  const contentRef = useRef(null);

  // The "Generuj email" button is embedded in the topic HTML (data-faq-action=
  // "generate-email"). Use a delegated click listener (CSP-safe, no inline onclick)
  // to open the email modal when it is clicked.
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return undefined;
    const onClick = (e) => {
      const trigger = e.target?.closest?.("[data-faq-action='generate-email']");
      if (trigger) {
        e.preventDefault();
        setEmailModalOpen(true);
      }
    };
    el.addEventListener("click", onClick);
    return () => el.removeEventListener("click", onClick);
  }, [content]);

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(topic?.emailTemplate || "");
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    } catch (_) {
      // clipboard not available – ignore
    }
  };

  const topic = useMemo(() => {
    const matches = normalizedTopics.filter(
      (item) => (item.urlKey || "") === (urlKey || "")
    );
    if (matches.length === 0) return null;
    if (matches.length === 1) return matches[0];
    const deduped = dedupeTopicsByUrlKeyPreferCustomer(matches);
    return deduped[0] ?? null;
  }, [normalizedTopics, urlKey]);
  const isServiceTopic = (topic?.section || "service") === "service";
  const isInternalTopic = (topic?.section || "service") === "internal";
  const accessDenied =
    topic &&
    ((isServiceTopic && isCustomerType2Or3) ||
      (isInternalTopic && !isMainAdmin));

  useEffect(() => {
    if (accessDenied || !topic?.file) {
      setContent("");
      setLoadError(false);
      return;
    }
    setLoadError(false);
    const path = `${PUBLIC_URL}/faq/topics/${topic.file}`;
    fetch(path)
      .then((res) => {
        if (!res.ok) throw new Error("Not found");
        return res.text();
      })
      .then((html) => {
        try {
          const doc = new DOMParser().parseFromString(html, "text/html");
          const bodyContent = doc.body?.innerHTML ?? html;
          setContent(bodyContent);
        } catch {
          setContent(html);
        }
      })
      .catch(() => {
        setLoadError(true);
        setContent("");
      });
  }, [accessDenied, topic?.file, urlKey]);

  useEffect(() => {
    if (printModalOpen && content) {
      if (!printTriggeredRef.current) {
        printTriggeredRef.current = true;
        triggerPrintWithContent(content);
      }
    } else {
      printTriggeredRef.current = false;
    }
  }, [printModalOpen, content]);

  if (!topic) {
    return (
      <Box sx={{ px: 0, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 2,
          }}
        >
          <Breadcrumbs aria-label="breadcrumb" sx={{ flex: "1 1 auto", minWidth: 0 }}>
            <Link to="/">{t("sidebar.menuItem.home")}</Link>
            <Link to="/dsok/faq" style={{ textDecoration: "none", color: "inherit" }}>
              {t("faq.title", { defaultValue: "FAQ" })}
            </Link>
            <Typography color="text.primary">{urlKey || "—"}</Typography>
          </Breadcrumbs>
        </Box>
        <Paper
          variant="outlined"
          sx={{
            bgcolor: "background.paper",
            p: 3,
            borderRadius: 1,
          }}
        >
          <Typography color="text.secondary">
            {t("faq.topicNotFound", { defaultValue: "Topic not found" })}
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (accessDenied) {
    return (
      <Box sx={{ px: 0, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 2,
          }}
        >
          <Breadcrumbs aria-label="breadcrumb" sx={{ flex: "1 1 auto", minWidth: 0 }}>
            <Link to="/">{t("sidebar.menuItem.home")}</Link>
            <Link to="/dsok/faq" style={{ textDecoration: "none", color: "inherit" }}>
              {t("faq.title", { defaultValue: "FAQ" })}
            </Link>
            <Typography color="text.primary">
              {topic.title || topic.urlKey || "—"}
            </Typography>
          </Breadcrumbs>
        </Box>
        <Paper
          variant="outlined"
          sx={{
            bgcolor: "background.paper",
            p: 3,
            borderRadius: 1,
          }}
        >
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {t("faq.accessDenied", { defaultValue: "Access denied." })}
          </Typography>
          <Link to="/dsok/faq" style={{ textDecoration: "none" }}>
            <Button color="primary" size="small" variant="outlined">
              {t("faq.title", { defaultValue: "FAQ" })}
            </Button>
          </Link>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 0, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 2,
        }}
      >
        <Breadcrumbs aria-label="breadcrumb" sx={{ flex: "1 1 auto", minWidth: 0 }}>
          <Link to="/">{t("sidebar.menuItem.home")}</Link>
          <Link to="/dsok/faq" style={{ textDecoration: "none", color: "inherit" }}>
            {t("faq.title", { defaultValue: "FAQ" })}
          </Link>
          <Typography color="text.primary">
            {topic.title || topic.urlKey || "—"}
          </Typography>
        </Breadcrumbs>
        {content && (
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0, flexWrap: "wrap" }}>
            {/* "Generuj email" button lives inside the document content (see the
                data-faq-action="generate-email" button in the topic HTML); a delegated
                click listener below opens the modal. */}
            <Tooltip title={t("faq.print", { defaultValue: "Print" })}>
              <span>
                <Button
                  color="primary"
                  size="small"
                  variant="outlined"
                  onClick={() => setPrintModalOpen(true)}
                  aria-label={t("faq.print", { defaultValue: "Print" })}
                  startIcon={<PrintOutlinedIcon fontSize="small" />}
                >
                  {t("faq.print", { defaultValue: "Print" })}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        )}
      </Box>

      {loadError && (
        <Typography color="error" sx={{ mb: 2 }}>
          {t("faq.contentLoadError", { defaultValue: "Content could not be loaded." })}
        </Typography>
      )}

      <Paper
        variant="outlined"
        sx={{
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          bgcolor: "background.paper",
          p: { xs: 2, sm: 3 },
          borderRadius: 1,
          overflow: "auto",
          "& h1": { typography: "h4", mb: 2 },
          "& h2": { typography: "h5", mt: 2, mb: 1 },
          "& h3": { typography: "h6", mt: 2, mb: 1 },
          "& p": { mb: 1.5 },
          "& ul": { pl: 3, mb: 1.5 },
          "& li": { mb: 0.5 },
          "& img": { maxWidth: "100%", height: "auto" },
          "& table": { maxWidth: "100%", display: "block", overflowX: "auto" },
          "& pre": { maxWidth: "100%", overflowX: "auto" },
        }}
      >
        <div ref={contentRef} dangerouslySetInnerHTML={{ __html: content || "" }} />
      </Paper>

      <Dialog
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t("faq.generateEmail", { defaultValue: "Generuj email" })}
          </Typography>
          <Box
            component="pre"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: "inherit",
              fontSize: "0.9rem",
              m: 0,
              mb: 2,
              p: 2,
              bgcolor: "action.hover",
              borderRadius: 1,
            }}
          >
            {topic?.emailTemplate || ""}
          </Box>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={() => setEmailModalOpen(false)}>
              {t("faq.close", { defaultValue: "Zavrieť" })}
            </Button>
            <Button
              variant="contained"
              startIcon={<ContentCopyOutlinedIcon fontSize="small" />}
              onClick={handleCopyEmail}
            >
              {emailCopied
                ? t("faq.copied", { defaultValue: "Skopírované" })
                : t("faq.copy", { defaultValue: "Kopírovať" })}
            </Button>
          </Stack>
        </Box>
      </Dialog>

      <Dialog
        open={printModalOpen}
        onClose={() => setPrintModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { p: 0, maxHeight: "90vh", overflow: "hidden" },
        }}
      >
        <Box
          sx={{
            overflow: "auto",
            p: 2,
            "& h1": { fontSize: "1.5rem", mb: 2 },
            "& h2": { fontSize: "1.25rem", mt: 2, mb: 1 },
            "& h3": { fontSize: "1.1rem", mt: 2, mb: 1 },
            "& p": { mb: 1.5 },
            "& ul": { pl: 3, mb: 1.5 },
            "& li": { mb: 0.5 },
            "& img": { maxWidth: "100%", height: "auto" },
          }}
          dangerouslySetInnerHTML={{ __html: content || "" }}
        />
      </Dialog>
    </Box>
  );
};

export default FAQDetail;
